"use client";

import { TypeHero } from "@contentful/types";
import { customImageLoader } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import { H1 } from "@yardzen/ui/components/h1";
import { H3 } from "@yardzen/ui/components/h3";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import Image from "next/image";

export function HeroShowcaseBackground({
  hero,
}: {
  hero: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const mobileImage = hero?.fields.mobileOnlyImages?.at(0);
  const cta = hero?.fields.CTAs?.[0];
  const ctaUrl = cta?.fields.url;
  const marketingImage = hero?.fields.displayOptions?.includes("suppress-title")
    ? "yes"
    : "no";
  const heroVariants = cva("text-brand-beige-lighter", {
    variants: {
      textColor: {
        white: "text-brand-beige-lighter",
        black: "text-brand-primary-darker",
        default: "text-brand-beige-lighter",
      },
      noBgOpacity: {
        yes: "",
        no: "bg-black/50",
      },
      maxHeight: {
        yes: "max-h-[450px]",
        no: "min-h-[480px]",
      },
    },
  });

  return (
    <section
      className={cn("relative", heroVariants({ maxHeight: marketingImage }))}
    >
      <div
        className={cn(
          "flex h-[92vh]",
          heroVariants({ maxHeight: marketingImage }),
        )}
      >
        <div
          className={cn(
            "absolute inset-0 z-20",
            heroVariants({ noBgOpacity: marketingImage }),
          )}
        />
        <Image
          src={`https:${hero?.fields.images?.at(0)?.fields.image?.fields.file?.url}`}
          alt={
            hero?.fields.images?.at(0)?.fields.image?.fields.description ?? ""
          }
          fill={true}
          className={cn(
            "absolute object-cover sm:!relative sm:!h-auto",
            mobileImage ? "hidden md:block" : "",
          )}
          loader={customImageLoader}
        />
        {mobileImage && (
          <Image
            src={`https:${mobileImage?.fields.image?.fields.file?.url}`}
            alt={mobileImage?.fields.image?.fields.description ?? ""}
            fill={true}
            className={cn(
              "absolute object-cover sm:relative",
              mobileImage ? "block md:hidden" : "",
            )}
            loader={customImageLoader}
          />
        )}
        <div
          className={cn(
            "absolute inset-0 z-30 flex items-center justify-center",
            heroVariants({ maxHeight: marketingImage }),
          )}
        >
          <div className="flex max-w-[940px] flex-col items-center gap-4 p-6">
            {hero.fields.eyebrow && (
              <H3
                className={cn(
                  "max-w-3xl text-center text-lg normal-case",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
              >
                {hero.fields.eyebrow}
              </H3>
            )}
            {hero.fields.title && marketingImage === "no" && (
              <div className="w-100 center-title text-center">
                <H1
                  className={cn(
                    "max-w-3xl text-center normal-case lg:text-6xl",
                    heroVariants({ textColor: hero?.fields.textColor }),
                  )}
                >
                  {hero.fields.title}
                </H1>
              </div>
            )}
            {hero.fields.subtitle && (
              <P
                noSpacing
                className={cn(
                  "max-w-2xl text-center text-xl leading-[140%]",
                  heroVariants({ textColor: hero?.fields.textColor }),
                )}
              >
                {hero.fields.subtitle}
              </P>
            )}
            {hero.fields.cta && (
              <Button className="w-full sm:w-auto " asChild>
                <Link
                  className="bg-brand-tertiary-50 text-brand-primary-darker whitespace-nowrap py-3 font-semibold"
                  href={ctaUrl}
                >
                  {hero.fields.cta?.fields.buttonText}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
